import { GalleryImage } from 'shared/components/gallery/gallery-image';
import { CardMediaPlaceholder } from 'shared/components/card-media-placeholder';
import { View } from 'react-native';
import { Text } from 'shared/components/text';
import { Pet } from 'shared/@generated/models';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { Props } from 'shared/@types';
import {
  getPetStatusDotClassName,
  getPetStatusTextClassName,
  getPetStatusVariant,
} from 'shared/library/pet-status';
import { PedigreeId, PedigreeRegistryButton } from 'shared/components/pedigree-id';
import {
  normalizePedigreeRegistrationId,
  type PedigreeRegistryLink,
} from 'shared/library/pedigree';

export function PetCard({
  pet,
  className,
  imageClassName,
  onImagePress,
}: Props<{ pet: Pet; imageClassName?: string; onImagePress?: () => void }>) {
  const { t } = useTranslation('components');
  const registrationId = normalizePedigreeRegistrationId(pet.registration_id);

  return (
    <View
      className={clsx(
        'border-brand-brown-dark/15 flex-row items-center rounded border bg-white',
        className,
      )}
    >
      <GalleryImage
        src={pet.avatar}
        alt={pet.name}
        containerClassName={'h-28 w-1/5 max-w-32 flex-1 shrink-0 rounded-l'}
        imageClassName={clsx('rounded-l object-cover', imageClassName)}
        sizeType={'card'}
        sizes="128px"
        onPress={onImagePress}
        testID={`pet-image-${pet.id}`}
      />

      <View className={'flex-1 items-start px-4 py-3'}>
        <Text className={'text-brand-brown-dark font-black'} numberOfLines={1}>
          {pet.name}
        </Text>
        <Text className={'text-brand-brown-dark/70 text-sm'}>
          {t(`common:enums.gender.${pet.gender}_${pet.listing.animal}`)}
        </Text>
        <PetStatusBadge pet={pet} className={'border-brand-brown-dark/15 mt-2 border'} />
        {registrationId && <PedigreeId registrationId={registrationId} className={'mt-2'} />}
      </View>
    </View>
  );
}

export function PetStatusBadge({ pet, className }: { pet: Pet; className?: string }) {
  const { t } = useTranslation('components');
  const statusVariant = getPetStatusVariant(pet);

  return (
    <View
      className={clsx(
        'flex-row items-center self-start rounded-full bg-white px-2 py-0.5',
        className,
      )}
    >
      <View className={clsx('mr-1.5 size-1.5 rounded-full', getPetStatusDotClassName(pet))} />
      <Text className={clsx('text-xs font-semibold', getPetStatusTextClassName(pet))}>
        {statusVariant === 'deleted' && t('pet_card.deleted')}
        {statusVariant === 'deceased' && t('pet_card.deceased')}
        {statusVariant !== 'deleted' &&
          statusVariant !== 'deceased' &&
          t(`common:enums.pet_status.${statusVariant}`)}
      </Text>
    </View>
  );
}

export function AnimalCard({
  className,
  image,
  imageAlt,
  imageTestID,
  onImagePress,
  title,
  subtitle,
  statusPet,
  registrationId,
  registryLink,
  registryLinkTestID,
}: Props<{
  image?: Pet['avatar'] | null;
  imageAlt: string;
  imageTestID?: string;
  onImagePress?: () => void;
  title: string;
  subtitle?: string;
  statusPet?: Pet;
  registrationId?: string | null;
  registryLink?: PedigreeRegistryLink | null;
  registryLinkTestID?: string;
}>) {
  const imageClassName = 'aspect-4/3 w-full rounded';

  return (
    <View
      className={clsx('border-brand-brown-dark/15 flex-1 rounded border bg-white p-2', className)}
    >
      <View className={'relative'}>
        {image ? (
          <GalleryImage
            src={image}
            alt={imageAlt}
            containerClassName={imageClassName}
            imageClassName={'rounded object-cover'}
            sizeType={'card'}
            sizes="(min-width: 768px) 240px, 50vw"
            onPress={onImagePress}
            testID={imageTestID}
          />
        ) : (
          <CardMediaPlaceholder
            className={imageClassName}
            iconClassName={'text-brand-brown-dark/35 size-10'}
            panelClassName={'p-0'}
          />
        )}
        {statusPet && (
          <View pointerEvents={'none'} className={'absolute left-1.5 top-1.5'}>
            <PetStatusBadge pet={statusPet} />
          </View>
        )}
      </View>

      <View className={'flex-1 px-0.5 pt-2'}>
        <Text className={'text-brand-brown-dark font-black'} numberOfLines={1}>
          {title}
        </Text>
        {subtitle && (
          <Text className={'text-brand-brown-dark/75'} numberOfLines={1}>
            {subtitle}
          </Text>
        )}

        {registrationId && <PedigreeId registrationId={registrationId} className={'mt-2'} />}

        {registryLink && (
          <View className={'mt-auto pt-2'}>
            <PedigreeRegistryButton registryLink={registryLink} testID={registryLinkTestID} />
          </View>
        )}
      </View>
    </View>
  );
}
